<script setup lang="ts">
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import type { UserFieldValue } from '@@/types'
import type { PostPropertyDisplayAttributes } from '@@/vuecomponents/SurfacePostProperties.vue'
import { computed, ref } from 'vue'
import { useNativeAppStore } from '@@/pinia/native_app'

const props = withDefaults(
  defineProps<{
    darkMode: boolean
    postProperty: PostPropertyDisplayAttributes
    propertyValueClass?: string
    as?: string
  }>(),
  {
    propertyValueClass: 'text-body-small',
    as: 'dd',
  },
)

const nativeAppStore = useNativeAppStore()

function assertIsUserFieldValue(value: unknown): asserts value is UserFieldValue {
  if (
    typeof value !== 'object' ||
    value == null ||
    !Object.keys(value).every((key) => key === 'username' || key === 'name' || key === 'id')
  ) {
    throw new Error('Expected UserFieldValue but got: ' + typeof value)
  }
}

const getProfileUrl = (value: UserFieldValue) => {
  return `${window.location.origin}/${value.username}`
}

const userFieldValue = computed(() => {
  assertIsUserFieldValue(props.postProperty.formattedValue)
  return props.postProperty.formattedValue
})

const userFieldHtmlRef = ref<HTMLElement | null>(null)

const getNameFromUserFieldValue = (value: UserFieldValue) => {
  if (value.name !== '' && value.name != null) return value.name
  if (value.username !== '' && value.username != null) return value.username
  return __('User not found')
}

const handleUserFieldValueClick = (event: MouseEvent) => {
  if (device.app) {
    event.preventDefault()
    nativeAppStore.openLink({
      url: getProfileUrl(userFieldValue.value),
      linkType: 'internal',
      contentCategory: 'page',
      contentSubcategory: 'profile',
      username: userFieldValue.value.username,
    })
  }
  // web will handle the default anchor tag behavior
}
</script>

<template>
  <!-- eslint-disable vue/no-v-html vue/no-v-text-v-html-on-component -->
  <component
    :is="as"
    ref="userFieldHtmlRef"
    :class="[
      'user-field-value',
      propertyValueClass,
      darkMode && 'text-light-text-100',
      !darkMode && 'text-dark-text-100',
    ]"
  >
    <a
      :href="getProfileUrl(userFieldValue)"
      target="_blank"
      rel="noopener nofollow ugc"
      @click="handleUserFieldValueClick"
    >
      {{ getNameFromUserFieldValue(userFieldValue) }}
    </a>
  </component>
</template>
<style>
.user-field-value a {
  @apply underline;
  @apply text-current;
  text-underline-position: from-font;
  text-decoration-thickness: from-font;
}
</style>
